import { Panel } from '../core/Panel';
import { Sim, SeedType, SEED_TYPES, SEED_COSTS, MAILBOX_DELIVERY_TIME } from '../../game/simulation';
import { ICON } from '../icons';

export class MailboxPanel extends Panel {
  private draft: Record<SeedType, number> = { wheat: 0, corn: 0, carrot: 0 };

  constructor(sim: Sim) {
    super(sim);
    // Wheel-to-adjust on a stepper row. Wheel up = +1 (shift = +10), down = -1.
    // We attach to the panel root directly because data-seed sits on inner
    // elements and we want the whole row to be wheel-active.
    this.root.addEventListener('wheel', (e: WheelEvent) => {
      const stepper = (e.target as HTMLElement).closest('.ui-stepper') as HTMLElement | null;
      if (!stepper || stepper.classList.contains('locked')) return;
      const seed = stepper.dataset.seed as SeedType | undefined;
      if (!seed) return;
      e.preventDefault();
      const sign = e.deltaY < 0 ? +1 : -1;
      const step = e.shiftKey ? 10 : 1;
      this.adjust(seed, sign * step);
    }, { passive: false });
  }
  id() { return 'mailbox'; }
  title() { return '📬 Mailbox'; }
  hashInputs() {
    const s = this.sim.state;
    return JSON.stringify({
      // Body reads `Math.ceil(m.eta)` — match that here so the displayed
      // countdown re-renders the moment the rounded value changes.
      mailbox: { pending: s.mailbox.pending, eta: Math.ceil(s.mailbox.eta) },
      money: Math.floor(s.res.money),
      draft: this.draft,
      level: s.prog.level,
    });
  }
  body() {
    const m = this.sim.state.mailbox;
    if (m.pending) {
      const lines = m.pending.map(l => `${l.qty}× ${l.seed}`).join(', ');
      return `
        <div class="ui-section">
          <div class="ui-section__title">In transit</div>
          <div class="ui-row"><span>${lines}</span></div>
          <div class="ui-row"><span>ETA</span><strong>${Math.ceil(m.eta)}s</strong></div>
        </div>
        <div class="ui-tip">Wait for delivery, then come back to order more.</div>
      `;
    }
    const lv = this.sim.state.prog.level;
    const seedGlyph: Record<SeedType, string> = { wheat: ICON.wheat, corn: ICON.corn, carrot: ICON.carrot };
    const steppers = SEED_TYPES.map(seed => {
      const locked = seed !== 'wheat' && lv < 2;
      const q = this.draft[seed];
      const disAll = locked ? 'disabled' : '';
      const disNeg = (locked || q <= 0) ? 'disabled' : '';
      return `
        <div class="ui-stepper ${locked ? 'locked' : ''}" data-seed="${seed}">
          <div class="ui-stepper__label"><span class="glyph glyph-inline">${seedGlyph[seed]}</span> ${seed}${locked ? ' (LV2)' : ''}</div>
          <div class="ui-stepper__cost">${SEED_COSTS[seed]}<span class="glyph glyph-inline">${ICON.coin}</span></div>
          <button class="ui-stepper__btn" data-delta="-10" data-seed="${seed}" ${disNeg}>−10</button>
          <button class="ui-stepper__btn" data-delta="-1"  data-seed="${seed}" ${disNeg}>−</button>
          <div class="ui-stepper__qty">${q}</div>
          <button class="ui-stepper__btn" data-delta="+1"  data-seed="${seed}" ${disAll}>+</button>
          <button class="ui-stepper__btn" data-delta="+10" data-seed="${seed}" ${disAll}>+10</button>
        </div>`;
    }).join('');
    const total = SEED_TYPES.reduce((s, k) => s + this.draft[k] * SEED_COSTS[k], 0);
    const can = total > 0 && this.sim.state.res.money >= total;
    return `
      ${steppers}
      <div class="ui-row"><strong>Total</strong><strong>${total}<span class="glyph glyph-inline">${ICON.coin}</span></strong></div>
      <button class="ui-btn ui-btn--primary ui-btn--block" data-act="order" ${can ? '' : 'disabled'}>
        Place order — arrives in ${MAILBOX_DELIVERY_TIME}s
      </button>
      <div class="ui-tip">Coins are charged on order. Delivery truck brings seeds to the mailbox.</div>
    `;
  }
  onClick(e: Event) {
    const t = e.target as HTMLElement;
    const btn = t.closest('[data-delta]') as HTMLElement | null;
    if (btn) {
      const seed = btn.dataset.seed as SeedType | undefined;
      const delta = parseInt(btn.dataset.delta ?? '0', 10);
      if (seed && delta) this.adjust(seed, delta);
      return;
    }
    const actEl = t.closest('[data-act]') as HTMLElement | null;
    if (actEl?.dataset.act === 'order' && !(actEl as HTMLButtonElement).disabled) {
      const lines = SEED_TYPES.map(seed => ({ seed, qty: this.draft[seed] })).filter(l => l.qty > 0);
      if (this.sim.placeSeedOrder(lines)) {
        this.draft = { wheat: 0, corn: 0, carrot: 0 };
      }
    }
  }

  private adjust(seed: SeedType, delta: number) {
    this.draft[seed] = Math.max(0, (this.draft[seed] ?? 0) + delta);
    this.tick();
  }

  // Override tick to force re-render after draft mutation
  tick() {
    if (this.isVisible()) super.tick();
  }
}
